import React from 'react'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import Avatar from '@mui/material/Avatar'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'

import { api, getApiErrorMessage } from '~/api'
import { useApp } from '~/context/AppContext'
import { useLanguage } from '~/common/hooks'
import { formatDate } from '~/utils/formatters'
import { useInfiniteQuery } from '@tanstack/react-query'

interface ConversationType {
  id: number
  store: number
  customer: number | null
  agent: number | null
  conversation_id: string
  date_created: string
  date_updated: string
  data: Record<string, any> | null
}

interface TurnType {
  id: number
  conversation: number
  sender: number
  content: string
  timestamp: string
  data: Record<string, any> | null
}

export default function Conversations() {
  const { stores = [] } = useApp()
  const language = useLanguage()
  const store = stores[0]
  const storeIdStr = store ? String(store.id) : ''
  const [selectedConv, setSelectedConv] = React.useState<ConversationType | null>(null)

  const turnsContainerRef = React.useRef<HTMLDivElement>(null)
  const [autoScroll, setAutoScroll] = React.useState(true)

  const {
    data: convData,
    status: convStatus,
    error: convError,
  } = api.conversations.list.useQuery(
    ['conversations', { storeId: storeIdStr }],
    { params: { storeId: storeIdStr } },
    { enabled: !!store }
  )

  const conversations = Array.isArray(convData?.body?.results)
    ? convData.body.results
    : Array.isArray(convData?.body)
      ? convData.body
      : [];
  const sortedConversations = conversations.sort(
    (a: ConversationType, b: ConversationType) => new Date(b.date_updated).getTime() - new Date(a.date_updated).getTime()
  );


  const {
    data: turnsPages,
    status: turnsStatus,
    error: turnsError,
    fetchNextPage,
    hasPreviousPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchTurns,
  } = useInfiniteQuery(
    ['conversations', 'turns', { conversationId: selectedConv?.id ?? 0 }],
    async ({ pageParam }) => {
      if (!selectedConv?.id) return { results: [], next: null };
      const params = { conversationId: selectedConv.id };
      const query: { limit?: number; offset?: number } = { limit: 25};
      if (pageParam) {
        query.offset = pageParam;
      }
      const res = await api.conversations.turns.query({ params, query });
      res.body.results.reverse()
      return res.body;
    },
    { 
      enabled: !!selectedConv,
      getNextPageParam: (lastPage) => {
        if (!lastPage || !lastPage.next) return undefined;
        try {
          const url = new URL(lastPage.next);
          const offset = url.searchParams.get('offset');
          return offset ? Number(offset) : undefined;
        } catch {
          return undefined;
        }
      }
    }
  )

  const sortedTurns = React.useMemo(() => {
    if (!turnsPages?.pages) return [];
    const allTurns = turnsPages.pages.reverse().flatMap((page: any) => Array.isArray(page.results) ? page.results : []);
    return allTurns.sort((a: TurnType, b: TurnType) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [turnsPages]);

  React.useEffect(() => {
    if (turnsContainerRef.current && autoScroll && sortedTurns.length > 0) {
      turnsContainerRef.current.scrollTop = turnsContainerRef.current.scrollHeight
    }
  }, [sortedTurns, selectedConv, autoScroll])

  // Reset scroll and refetch when switching conversation
  React.useEffect(() => {
    if (turnsContainerRef.current) {
      turnsContainerRef.current.scrollTop = turnsContainerRef.current.scrollHeight
    }
    setAutoScroll(true)
    refetchTurns()
  }, [selectedConv])

  // Scroll event handler for infinite scroll
  const onTurnsScroll = React.useCallback(() => {
    const container = turnsContainerRef.current
    if (!container) return
    // If near the top, fetch next page
    if (container.scrollTop < 100 && hasNextPage && !isFetchingNextPage) {
      const prevHeight = container.scrollHeight
      fetchNextPage().then(() => {
        // Maintain scroll position after loading more
        setTimeout(() => {
          if (container && container.scrollHeight > prevHeight) {
            container.scrollTop = container.scrollHeight - prevHeight
          }
        }, 0)
      })
    }
    // If user scrolls to bottom, enable auto-scroll
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 10) {
      setAutoScroll(true)
    } else {
      setAutoScroll(false)
    }
  }, [fetchNextPage, hasNextPage, hasPreviousPage, isFetchingNextPage])

  // Select first conversation by default
  React.useEffect(() => {
    if (!selectedConv && sortedConversations.length > 0) {
      setSelectedConv(sortedConversations[0])
    }
  }, [sortedConversations, selectedConv])


  React.useEffect(() => {
    if (!selectedConv && sortedConversations.length > 0) {
      setSelectedConv(sortedConversations[0]);
    }
  }, [sortedConversations, selectedConv]);


  return (
    <Box sx={{
      display: 'flex',
      overflow: 'hidden',
      backgroundColor: 'white'
    }}>
      <Box
        sx={{
          minWidth: 300,
          borderRight: '1px solid',
          borderColor: 'divider',
          overflowY: 'auto',
        }}
      >
        {convStatus === 'loading' ? (
          <Stack spacing={1} p={2}>
            {Array.from({ length: 5 }).map((_, idx) => (
              <Skeleton key={idx} variant="rectangular" height={48} />
            ))}
          </Stack>
        ) : convStatus === 'error' ? (
          <Typography variant="body2" color="error" p={2}>
            {getApiErrorMessage(convError)}
          </Typography>
        ) : (
          <List disablePadding>
            {sortedConversations.map((conv: ConversationType) => {
              const name = conv.data?.customerName ?? 'Customer'
              return (
                <ListItemButton
                  key={conv.id}
                  selected={selectedConv?.id === conv.id}
                  onClick={() => setSelectedConv(conv)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: (theme) => theme.palette.secondary.light,
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.secondary.light,
                      },
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar>{name.charAt(0)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={name}
                    secondary={formatDate(conv.date_updated, { locale: language })}
                  />
                </ListItemButton>
              )
            })}
          </List>
        )}
      </Box>
      <Box sx={{ height: 'calc(100vh - 80px)', maxHeight: 'calc(100vh - 80px)', overflow: 'hidden', display: 'flex', flexDirection: 'column', backgroundColor: 'white', width: '100%' }}>
        {selectedConv ? (
          <>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}>
                  <Typography variant="subtitle1">
                    {selectedConv.data?.customerName ?? 'Customer'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(selectedConv.date_updated, { locale: language })}
                  </Typography>
                </Box>
              </Stack>
            </Box>
            {/* Turns list */}
            {turnsStatus === 'loading' ? (
              <Stack spacing={1} p={2}>
                {Array.from({ length: 5 }).map((_, idx) => (
                  <Skeleton
                    key={idx}
                    variant="rectangular"
                    height={48}
                    width="60%"
                  />
                ))}
              </Stack>
            ) : turnsStatus === 'error' ? (
              <Typography variant="body2" color="error" p={2}>
                {getApiErrorMessage(turnsError)}
              </Typography>
            ) : (
              <Box ref={turnsContainerRef} onScroll={onTurnsScroll} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
                {isFetchingNextPage && (
                  <Typography variant="caption" color="text.secondary" align="center">Loading earlier messages...</Typography>
                )}
                {console.log('sortedTurns', sortedTurns, 'turnsPages', turnsPages)}
                {sortedTurns.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center">
                    No messages yet.
                  </Typography>
                ) : (
                  sortedTurns.map((turn) => {
                    const isAgent = turn.sender === selectedConv.agent;
                    // Use turn.id as the key; backend must guarantee uniqueness and no overlap
                    return (
                      <Box
                        key={turn.id}
                        sx={{
                          alignSelf: isAgent ? 'flex-end' : 'flex-start',
                          maxWidth: '60%',
                          marginBottom: "0.2rem"
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: "0.75rem",
                            borderRadius: 2,
                            bgcolor: isAgent ? 'secondary.main' : 'grey.200',
                            color: isAgent ? 'secondary.contrastText' : 'text.primary',
                          }}
                        >
                          <Typography variant="body2">
                            {turn.content}
                          </Typography>
                        </Paper>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textAlign: isAgent ? 'right' : 'left' }}
                        >
                          {formatDate(turn.timestamp, { locale: language })}
                        </Typography>
                      </Box>
                    );
                  })
                )}
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Select a conversation
            </Typography>
          </Box>
        )}
      </Box>

    </Box>
  )
}
